import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { createTheme, MuiThemeProvider, withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { toUpper, startCase } from 'lodash';
import LinearProgress from '@material-ui/core/LinearProgress';
import MUIDataTable from 'mui-datatables';
import moment from "moment";
import numeral from 'numeral';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import fetch from 'unfetch';

const theme = createTheme({
  overrides : {
    MUIDataTable         : {
      responsiveScroll : {
        maxHeight : '700px !important',
      },
    },
    MUIDataTableBodyCell : {
      root : {
        width : '130px',
      },
    },
  },
});

const generateColumns = (motivationFilters, classes, print_view, catastrophic_planning_word, deal_room_branded_name) => { 
  let columns = [
    'Name',
    'Email',
    'Transition Goals',
    'Branch',
    'Division',
    'Region',
    'Manager',
    'Approval State',
    'Hidden',
    'Disapproved Reason',
    'Age',
    'Industry Length of Service',
    'Years with Company',
    'Joined Date',
    'Total AUM',
    'Gross Production',
    'DCF Valuation'
  ].map(c => formatHeader(c, '#fffff', classes));
  if (motivationFilters.user_motivation == 'succession') {
    columns = columns.concat(
      [ 
        `${deal_room_branded_name} Created`,
        'Days Since',
        'Owner',
        'Status',
        'Matching', 
        'Invited', 
        'Reviewing Proposal', 
        'NDA', 
        'Negotiating' 
      ].map(c =>
        formatHeader(c, '#D5D8DC', classes)
      )
    );
  } else if (motivationFilters.user_motivation == 'growth') {
    columns = columns.concat(
      [ 
        'Created Date',
        'Days Since',
        'Status',
        'Suggested Matches',
        'Rolodex Matches',
        'Total Matches',
        'Matching', 
        'Reviewing', 
        'Proposal', 
        'NDA', 
        'Negotiating' 
      ].map(c => formatHeader(c, '#7FB3D5', classes))
    );
  }
  columns = columns.concat(
    [
      `${catastrophic_planning_word} Plan Preference`,
      `${catastrophic_planning_word} Plan Stage`,
      `${catastrophic_planning_word} Partners`,
    ].map(c => formatHeader(c, '#fffff', classes))
  );
  return columns;
};

const formatHeader = (column, color, classes) => {
  return {
    name    : column,
    options : {
      customHeadRender : (columnMeta, handleToggleColumn, sortOrder) => (
        <th
          key={columnMeta.index}
          onClick={() => handleToggleColumn(columnMeta.index)}
          className={classes.dealRoomHeaders}
          style={{
            background : color,
          }}>
          {columnMeta.name}
          <TableSortLabel active={sortOrder.name === columnMeta.name} direction={sortOrder.direction || 'asc'} />
        </th>
      ),
    },
  };
};

const formatCurrency = amount => {
  let result = amount >= 1000000 ? numeral(amount).format('($0,0a)') : numeral(amount).format('($0a)');
  return toUpper(result);
};

const getLegend = (motivationFilters, classes) => {
  if (motivationFilters.user_motivation == 'succession') {
    return (
      <Grid container style={{ marginBottom: '6px' }} justify="flex-end">
        <div className={classes.colorBox} style={{ background: '#D5D8DC' }} />
        <button className={classes.legendText}>- Succession Meeting Room</button>
      </Grid>
    );
  } else if (motivationFilters.user_motivation == 'growth') {
    return (
      <Grid container style={{ marginBottom: '6px' }} justify="flex-end">
        <div className={classes.colorBox} style={{ background: '#7FB3D5' }} />
        <button className={classes.legendText}>- Growth Meeting Room</button>
      </Grid>
    );
  }
  return <div />;
};

const getOwnerName = (deals) => {
  if (!deals || !deals.length) return '-';
  
  const ownerNames = deals
    .map(deal => get(deal, 'owner.name'))
    .filter(name => name !== undefined && name !== null)
    .join(', ');
    
  return ownerNames || '-';
};

const MotivationTable = ({
  inventory_users,
  motivationFilters,
  classes,
  loading,
  print_view,
  catastrophic_planning_word,
  deal_room_branded_name,
}) => {

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [setData] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});

  useEffect(() => {
    fetch(`/api/data?page=${page}&limit=${rowsPerPage}`)
      .then(response => response.json())
      .then(data => setData(data));
  }, [page, rowsPerPage]);


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Custom CSV download function that only includes visible columns
  const customCSVDownload = (buildHead, buildBody, columns, data) => {
    // Get visible column indices and names
    const visibleColumnIndices = [];
    const visibleColumnNames = [];
    
    columns.forEach((col, index) => {
      if (col.display !== 'false') {
        visibleColumnIndices.push(index);
        visibleColumnNames.push(col.name);
      }
    });
    
    // Build CSV content
    const csvContent = [
      // Header row
      visibleColumnNames.join(','),
      // Data rows - only include data for visible columns
      ...data.map(row => {
        return visibleColumnIndices.map(colIndex => {
          const value = row.data[colIndex] || '';
          // Escape commas and quotes in CSV values
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',');
      })
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    const motivation = motivationFilters.user_motivation || 'all';
    const date = new Date().toISOString().split('T')[0];
    const filename = `motivation_report_${motivation}_${date}.csv`;
    
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Return false to prevent default MUIDataTable download
    return false;
  };

  const data = (inventory_users || []).map(inv => {
    let row = [
      inv.name || '-',
      inv.email || '-',
      inv.transition_goals || '-',
      inv.branch || '-',
      inv.division || '-',
      inv.region || '-',
      inv.manager_name || '-',
      inv.approval_state || '-',
      get(inv, 'user.hidden') ? 'True' : 'False',
      get(inv, "user.disapproved_reason_comment") || '-',
      inv.age || '-',
      inv.industry_length_of_service || '-',
      inv.years_with_company || '-',
      inv.user.created_at ? new Date(inv.user.created_at).toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric', 
        year: 'numeric'
      }) : '-',
      formatCurrency(inv.total_assets),
      formatCurrency(inv.user_gross_production),
      formatCurrency(inv.dcf_valuation),
    ];
    if (motivationFilters.user_motivation == 'succession') {
      row = row.concat([ 
        inv.creation_date,
        inv.creation_date ? moment(inv.creation_date).fromNow() : '',
        get(inv, 'deals', []).length > 0 ? getOwnerName(inv.deals) : '-',
        inv.status,
        inv.matching,
        inv.invited,
        inv.reviewing_proposal,
        inv.nda_out_for_review,
        inv.negotiating,
      ]);
    } else if (motivationFilters.user_motivation == 'growth') {
      row = row.concat([ 
        inv.creation_date,
        inv.creation_date ? moment(inv.creation_date).fromNow() : '',
        inv.status,
        get(inv, "deal_interests_workflow_state_suggested_match_count") || 0,
        get(inv, "deal_interests_workflow_state_following_count") || 0,
        get(inv, "deal_interests_count") || 0, 
        inv.di_matching,
        inv.reviewing,
        inv.proposal,
        inv.nda,
        inv.di_negotiating,
       ]);
    }
    row = row.concat([ 
      startCase(inv.cat_plan_preference), 
      inv.cat_plan_stage, 
      inv.cat_partners 
    ]);

    return row;
  });

  if (loading) {
    return (
      <div className={classes.loadingCard}>
        <LinearProgress />
      </div>
    );
  }

  return (
    <MuiThemeProvider theme={theme}>
      {print_view && getLegend(motivationFilters, classes)}
      <MUIDataTable
        className={print_view ? classes.print : classes.table}
        data={data}
        columns={generateColumns(motivationFilters, classes, print_view, catastrophic_planning_word, deal_room_branded_name)}
        options={{
          filter             : false,
          print              : false,
          download           : !print_view,
          downloadOptions    : !print_view ? {
            filename: `motivation_report_${motivationFilters.user_motivation || 'all'}_${new Date().toISOString().split('T')[0]}.csv`,
            separator: ',',
            filterOptions: {
              useDisplayedColumnsOnly: true,
              useDisplayedRowsOnly: true,
            }
          } : undefined,
          onDownload: !print_view ? customCSVDownload : undefined,
          viewColumns        : !print_view,
          selectableRows     : 'none',
          fixedHeader        : true,
          responsive         : 'standard',
          rowsPerPage        : rowsPerPage,
          rowsPerPageOptions : print_view ? [] : [ 10, 15, 100 ],
          customToolbar      : () => getLegend(motivationFilters, classes),
          page: page,
          onChangePage: handleChangePage,
        }}
      />
    </MuiThemeProvider>
  );
};

const styles = {
  root            : {
    width     : '100%',
    overflowX : 'auto',
  },
  table           : {
    minWidth : 650,
  },
  print           : {
    width : 'fit-content',
  },
  container       : { display: 'flex', flexDirection: 'column', padding: 25 },
  menu            : { display: 'flex', width: '100%', alignItems: 'center', marginRight: 6 },
  datepicker      : { display: 'flex', flexDirection: 'row', alignItems: 'baseline' },
  bigNumber       : { fontSize: 50 },
  subHeader       : { fontWeight: 'normal', marginBottom: 0 },
  numberLabel     : { fontSize: 16 },
  selectatron     : { marginRight: 10 },
  formControl     : {
    margin   : 2,
    minWidth : 120,
  },
  loadingCard     : {
    border  : '1px #dadada solid',
    margin  : 12,
    padding : '42px 12px',
  },
  colorBox        : {
    width        : '55px',
    height       : '32px',
    borderRadius : '3px',
    display      : 'inline-block',
  },
  legendText      : {
    background : '#fff',
    border     : 'none',
    cursor     : 'default',
  },
  dealRoomHeaders : {
    padding  : '16px',
    top      : '0px',
    zIndex   : '100',
    position : 'sticky',
  },
};

MotivationTable.propTypes = {
  inventory_users            : PropTypes.array,
  motivationFilters          : PropTypes.object.isRequired,
  classes                    : PropTypes.object.isRequired,
  catastrophic_planning_word : PropTypes.string,
  deal_room_branded_name     : PropTypes.string,
};

MotivationTable.defaultProps = {
  inventory_users            : [],
  catastrophic_planning_word : 'Catastrophic',
  deal_room_branded_name     : 'Deal Room',
};

const mapStateToProps = state => ({
  motivationFilters          : state.motivationFilters,
  inventory_users            : state.inventory_users.inventory_users,
  inventory_user_count       : state.inventory_users.inventory_user_count,
  transition_goals           : state.inventory_users.transition_goals,
  print_view                 : state.inventory_users.print_view,
  catastrophic_planning_word : state.inventory_users.catastrophic_planning_word,
  deal_room_branded_name     : state.inventory_users.deal_room_branded_name,
});

const MotivationTableWithStyles = withStyles(styles)(MotivationTable);
export default connect(mapStateToProps)(MotivationTableWithStyles);
